import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';
// One key factory shared with the rail, so an invalidation here can never
// drift from the key the rail's counts are cached under (#710).
import { mailFolderKeys } from './useMailFolders';
import { notifyBellChanged } from '../lib/bellLinkage';
import { removeThreadsContaining } from './threadCaches';
import { withoutSignatureBlocks } from '../lib/editorSafeHtml';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/** Every page the provider sync walks in one click, per platform. */
const SYNC_MAX_PAGES = 5;
const SYNC_PAGE_SIZE = 100;

/**
 * Pages the QUIET background pass walks, per platform.
 *
 * One, not five. Both providers return newest-first, so everything that has
 * arrived since the last pass is on page 1 — pages 2-5 only re-walk history
 * this mailbox already holds, and the dedup query throws the results away. At
 * the five-minute cadence that waste was invisible; at sixty seconds it would
 * be ten provider calls a minute per open tab, per platform, forever.
 *
 * The explicit Sync button and the first-mount sync keep the full walk: those
 * are the paths that must be able to reach back into a mailbox.
 */
export const SYNC_BACKGROUND_MAX_PAGES = 1;

/**
 * Unified personal inbox emails — single source of truth.
 * Replaces useInboxEmails + useGmailEmails + useOutlookEmails + useTrackedEmails.
 * Filters are passed server-side as query params.
 *
 * @param {number} page - 1-indexed page number
 * @param {number} pageSize - items per page (default 40)
 * @param {object} filters - { search, sender, dateFrom, dateTo, replied, unread, provider, direction, sortBy }
 */
export function usePersonalInboxEmails(page = 1, pageSize = 40, filters = {}) {
    const { getAuthHeader } = useAuth();

    const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
    });
    if (filters.search) params.set('search', filters.search);
    if (filters.sender) params.set('sender', filters.sender);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.replied !== undefined && filters.replied !== null)
        params.set('replied', String(filters.replied));
    if (filters.unread !== undefined && filters.unread !== null)
        params.set('unread', String(filters.unread));
    if (filters.starred !== undefined && filters.starred !== null)
        params.set('starred', String(filters.starred));
    // True-only: unchecked means "do not filter", not "has no attachment".
    if (filters.hasAttachment) params.set('hasAttachment', 'true');
    if (filters.provider) params.set('provider', filters.provider);
    // #646 — the rail's tag filter (a tag_id); composes with the folder.
    if (filters.tag) params.set('tag', filters.tag);
    // The folder view. Sent on every request rather than only when it differs
    // from Inbox: the backend's default IS Inbox, so omitting it would make
    // "All mail" silently list the Inbox instead.
    if (filters.folder) params.set('folder', filters.folder);
    if (filters.direction) params.set('direction', filters.direction);
    // Backend whitelists sort_by to {comm_date, subject, from_email}; default
    // is comm_date. Only send when explicitly set to avoid an empty param.
    if (filters.sortBy && filters.sortBy !== 'comm_date') {
        params.set('sort_by', filters.sortBy);
    }

    return useQuery({
        queryKey: ['personal-inbox', 'emails', page, pageSize, filters],
        queryFn: async () => {
            const res = await apiFetch(
                `/inbox/personal/emails?${params.toString()}`,
                getAuthHeader()
            );
            // Endpoint wraps payload in { data: {...} } for consistency with
            // get_personal_thread and microsoft_inbox.py.
            return res.data;
        },
        placeholderData: (prev) => prev, // prevent flicker during refetch
        staleTime: 60_000,
        refetchInterval: 60_000, // poll for new emails
    });
}

/**
 * Combined sync mutation — pulls from Gmail and/or Outlook, whichever is
 * connected, and walks the provider's pages rather than stopping after the
 * first one.
 *
 * Both providers return a cursor (Gmail a `next_page_token`, Outlook a
 * `next_skip` + `has_more`) that the previous implementation discarded, so a
 * mailbox could never yield more than a single page no matter how many times
 * you clicked Sync. Bounded by SYNC_MAX_PAGES so one click cannot walk an
 * entire multi-year mailbox in a single request chain.
 *
 * Promise.allSettled so one platform's failure doesn't block the other.
 */
export function usePersonalInboxSync() {
    const queryClient = useQueryClient();
    const { getAuthHeader } = useAuth();

    return useMutation({
        mutationFn: async ({
            gmailConnected,
            outlookConnected,
            maxPages = SYNC_MAX_PAGES,
        }) => {
            const headers = getAuthHeader();

            const syncGmail = async () => {
                let pageToken;
                let synced = 0;
                let repaired = 0;
                for (let i = 0; i < maxPages; i += 1) {
                    const { data } = await axios.get(`${API}/oauth/gmail/sync`, {
                        headers,
                        params: {
                            max_results: SYNC_PAGE_SIZE,
                            ...(pageToken ? { page_token: pageToken } : {}),
                        },
                    });
                    synced += data?.synced || 0;
                    repaired += data?.repaired || 0;
                    pageToken = data?.next_page_token;
                    if (!pageToken) break;
                }
                return { synced, repaired };
            };

            const syncOutlook = async () => {
                let skip = 0;
                let synced = 0;
                for (let i = 0; i < maxPages; i += 1) {
                    const { data } = await axios.get(`${API}/oauth/outlook/sync`, {
                        headers,
                        params: { max_results: SYNC_PAGE_SIZE, skip },
                    });
                    synced += data?.synced || 0;
                    if (!data?.has_more) break;
                    skip = data?.next_skip ?? skip + SYNC_PAGE_SIZE;
                }
                return { synced, repaired: 0 };
            };

            const jobs = [];
            const labels = [];
            if (gmailConnected) {
                jobs.push(syncGmail());
                labels.push('Gmail');
            }
            if (outlookConnected) {
                jobs.push(syncOutlook());
                labels.push('Outlook');
            }

            const results = await Promise.allSettled(jobs);
            const synced = results.reduce(
                (sum, r) => sum + (r.status === 'fulfilled' ? r.value.synced : 0),
                0
            );
            const repaired = results.reduce(
                (sum, r) => sum + (r.status === 'fulfilled' ? r.value.repaired : 0),
                0
            );
            const failedLabels = labels.filter(
                (_, i) => results[i]?.status === 'rejected'
            );

            return { synced, repaired, failed: failedLabels.length, failedLabels, labels };
        },
        onSuccess: () => {
            // Invalidate page 1 only — this triggers a refetch that picks up any
            // server-side mutations (read-receipts, reply-state) we don't know about.
            // Pages 2+ stay cached so the user's accumulated scroll position survives.
            queryClient.invalidateQueries({
                queryKey: ['personal-inbox', 'emails'],
                exact: false,
                predicate: (q) => q.queryKey[2] === 1, // page === 1
            });
            // `total` used to be bumped here by the sync's message count, to
            // move the footer without a round-trip. Since #521 the list counts
            // CONVERSATIONS and the sync still counts MESSAGES, so adding one
            // to the other inflates the footer — and because only page 1 is
            // refetched above, the inflation on pages 2+ compounds with every
            // sync instead of being corrected. A footer that is briefly stale
            // on a page the user has scrolled past is the smaller error, and
            // the page-1 refetch already carries the true count.
        },
    });
}

/**
 * Reply to a personal-inbox message (issue #509 / D1).
 *
 * Posts to the dedicated reply endpoint rather than /communications or the
 * oauth send endpoints: the backend resolves the sending mailbox from the
 * message being replied to, so the payload carries NO `provider` and NO `from`
 * key — the client's sender selection must not be able to disagree with the
 * mailbox that received the message.
 *
 * `commId` is destructured out of the payload before posting; everything else
 * rides through as the request body.
 */
export function useReplyToPersonalMessage() {
    const queryClient = useQueryClient();
    const { getAuthHeader } = useAuth();

    return useMutation({
        mutationFn: async ({ commId, provider: _ignoredProvider, from: _ignoredFrom, ...payload }) => {
            // provider/from are stripped, not forwarded: the server picks the
            // sending mailbox from the message row (D1) — a stale caller that
            // still passes them must not be able to influence it.
            // #970 audit F09 — no signature marker or id in sent mail.
            const { data } = await axios.post(
                `${API}/inbox/personal/messages/${encodeURIComponent(commId)}/reply`,
                { ...payload, body: withoutSignatureBlocks(payload.body) },
                { headers: getAuthHeader() }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', 'thread'] });
            // The personal list uses a distinct cache namespace from the
            // unified inbox. Without this invalidation a successfully sent
            // reply stays absent from the personal list until its normal
            // polling/stale-time refresh.
            queryClient.invalidateQueries({ queryKey: ['personal-inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            queryClient.invalidateQueries({ queryKey: ['tracking'] });
        },
    });
}

/**
 * Mark personal-inbox messages read (issue #559).
 *
 * POSTs the ids to the dedicated read endpoint rather than PATCHing rows one
 * by one: opening a thread marks EVERY message in it at once, and the backend
 * scopes the write to the caller's owner+scope so a guessed id cannot mark
 * another mailbox's copy.
 *
 * onSuccess patches every cached ['personal-inbox','emails', ...] page before
 * invalidating, so the unread dot clears on the next paint instead of waiting
 * for the list's 60s refetchInterval. A row matches when its comm_id is in the
 * marked set OR any of its thread_comm_ids is — a row is a conversation (#521),
 * so reading one message clears the conversation row's dot too.
 */
export function useMarkPersonalInboxRead() {
    const queryClient = useQueryClient();
    const { getAuthHeader } = useAuth();

    return useMutation({
        mutationFn: async (commIds) => {
            const ids = Array.isArray(commIds) ? commIds : [commIds];
            if (!ids.length) return { matched: 0, already_read: 0 };

            const { data } = await axios.post(
                `${API}/inbox/personal/messages/read`,
                { comm_ids: ids },
                { headers: getAuthHeader() }
            );
            // Envelope: { ok: true, data: { matched, already_read } }.
            return data?.data ?? { matched: ids.length, already_read: 0 };
        },
        onSuccess: (_data, commIds) => {
            const marked = new Set(
                (Array.isArray(commIds) ? commIds : [commIds]).map(String)
            );

            // Cache patch first (synchronous): setQueriesData walks every
            // query under the ['personal-inbox','emails'] prefix. Only pages
            // that actually changed get a new identity.
            queryClient.setQueriesData(
                { queryKey: ['personal-inbox', 'emails'] },
                (page) => {
                    if (!page) return page;
                    const markRows = (rows) => {
                        if (!Array.isArray(rows)) return rows;
                        let changed = false;
                        const next = rows.map((row) => {
                            if (!row || row.is_read === true) return row;
                            const rowIds = row.thread_comm_ids?.length
                                ? row.thread_comm_ids
                                : [row.comm_id];
                            if (!rowIds.some((id) => marked.has(String(id)))) return row;
                            changed = true;
                            return { ...row, is_read: true };
                        });
                        return changed ? next : rows;
                    };

                    if (Array.isArray(page.emails)) {
                        const emails = markRows(page.emails);
                        return emails === page.emails ? page : { ...page, emails };
                    }
                    if (Array.isArray(page.entries)) {
                        const entries = markRows(page.entries);
                        return entries === page.entries ? page : { ...page, entries };
                    }
                    return page;
                }
            );

            // Then the same five keys useReplyToPersonalMessage invalidates —
            // the unified inbox, communications and tracking surfaces carry
            // the same read state.
            queryClient.invalidateQueries({ queryKey: ['inbox', 'thread'] });
            queryClient.invalidateQueries({ queryKey: ['personal-inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            queryClient.invalidateQueries({ queryKey: ['tracking'] });
        },
    });
}

/**
 * Remove messages from the personal inbox.
 *
 * A soft delete server-side: the row may still be a contact's timeline record
 * of that conversation, so it is tombstoned rather than destroyed. The
 * destructive path is disconnecting the mailbox, which purges outright.
 *
 * Takes an array in both cases and routes single ids to the single-delete
 * endpoint, so callers don't branch.
 */
export function useDeletePersonalMessages() {
    const queryClient = useQueryClient();
    const { getAuthHeader } = useAuth();

    return useMutation({
        mutationFn: async (commIds) => {
            const ids = Array.isArray(commIds) ? commIds : [commIds];
            if (!ids.length) return { deleted: 0, not_found: [] };

            if (ids.length === 1) {
                const { data } = await axios.delete(
                    `${API}/inbox/personal/messages/${encodeURIComponent(ids[0])}`,
                    { headers: getAuthHeader() }
                );
                return data?.data ?? { deleted: 1, not_found: [] };
            }

            const { data } = await axios.post(
                `${API}/inbox/personal/messages/bulk-delete`,
                { comm_ids: ids },
                { headers: getAuthHeader() }
            );
            return data?.data ?? { deleted: ids.length, not_found: [] };
        },
        onSuccess: (_data, commIds) => {
            queryClient.invalidateQueries({ queryKey: ['personal-inbox', 'emails'] });
            // The rail's counts live on their own lane key (#710): a purge
            // changes them, and this was the one personal write that never
            // refreshed the rail.
            queryClient.invalidateQueries({ queryKey: mailFolderKeys.lane(null) });
            // #803 — deleted for good: the caches of the threads that held
            // these messages go (a refetch could only 404), and the bell,
            // which the server has already reconciled, refetches.
            removeThreadsContaining(
                queryClient, ['inbox', 'thread'], Array.isArray(commIds) ? commIds : [commIds],
            );
            notifyBellChanged();
        },
    });
}
